import * as React from 'react'
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowUpDown, Download, Filter } from 'lucide-react'
import { toast } from 'sonner'

import { CopyButton } from '@/components/ui/copy-button'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { triggerDownload } from '@/lib/download'

/** The execute_sql tool payload. Shared by every consumer of a SQL result. */
export interface SqlResultData {
  data: Record<string, unknown>[]
  success: boolean
  count?: number
  error?: string
}

/**
 * Narrow a tool result to a SQL payload, or null. Accepts the raw JSON string
 * or an already-parsed value, so a caller that parsed once does not re-parse.
 */
export function parseSqlResult(value: unknown): SqlResultData | null {
  let parsed = value
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value)
    } catch {
      return null
    }
  }
  return parsed !== null &&
    typeof parsed === 'object' &&
    'success' in parsed &&
    Array.isArray((parsed as SqlResultData).data)
    ? (parsed as SqlResultData)
    : null
}

interface SqlResultsTableProps {
  result: SqlResultData
  className?: string
}

// Helper to create sortable headers
const createSortableHeader = (label: string) => {
  return ({ column }: { column: any }) => {
    const isSorted = column.getIsSorted()
    return (
      <Button
        variant="ghost"
        className="group !p-0 hover:!bg-transparent"
        onClick={() => {
          if (isSorted === 'asc') {
            column.toggleSorting(true)
          } else {
            column.toggleSorting(false)
          }
        }}>
        {label}{' '}
        <ArrowUpDown
          size={16}
          className={cn(
            'transition-opacity',
            isSorted ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}
        />
      </Button>
    )
  }
}

// Unavailable data component (matching DataTable)
const UnavailableData = ({
  className = 'text-gray-400',
}: {
  className?: string
}) => <span className={className}>-</span>

// Type detection helpers
const isUUID = (value: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  )
}

const isPhoneNumber = (value: string): boolean => {
  // Matches phone numbers like: 16509771405, 6509771405, (650) 977-1405
  return /^1?\d{10}$/.test(value.replace(/[\s\-()]/g, ''))
}

const formatPhoneNumber = (value: string): string => {
  const cleaned = value.replace(/[\s\-()]/g, '')

  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const areaCode = cleaned.slice(1, 4)
    const firstPart = cleaned.slice(4, 7)
    const secondPart = cleaned.slice(7)
    return `(${areaCode}) ${firstPart}-${secondPart}`
  } else if (cleaned.length === 10) {
    const areaCode = cleaned.slice(0, 3)
    const firstPart = cleaned.slice(3, 6)
    const secondPart = cleaned.slice(6)
    return `(${areaCode}) ${firstPart}-${secondPart}`
  }

  return value
}

// Format cell values with type-based detection
const formatCellValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) {
    return <UnavailableData />
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs ${
          value ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
        }`}>
        {value ? 'True' : 'False'}
      </span>
    )
  }

  // Handle numbers (before string checks)
  if (typeof value === 'number') {
    return <div className="text-right text-sm">{value.toLocaleString()}</div>
  }

  // Handle objects/arrays
  if (typeof value === 'object') {
    return (
      <code className="text-xs bg-gray-50 text-gray-700 px-2 py-1 rounded font-mono">
        {JSON.stringify(value)}
      </code>
    )
  }

  // String type detection
  if (typeof value === 'string') {
    const trimmed = value.trim()

    // UUID - monospace with copy button, no special color
    if (isUUID(trimmed)) {
      return (
        <div className="flex items-center gap-1">
          <span
            className="truncate max-w-[150px] font-mono text-xs"
            title={trimmed}>
            {trimmed}
          </span>
          <CopyButton
            content={trimmed}
            label="Copy UUID"
            onCopy={() => toast.success('Copied to clipboard')}
            onError={() => toast.error('Failed to copy')}
            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      )
    }

    // Phone number - purple badge
    if (isPhoneNumber(trimmed)) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs bg-purple-50 text-purple-700 whitespace-nowrap">
          {formatPhoneNumber(trimmed)}
        </span>
      )
    }

    // Date/timestamp - purple badge
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      try {
        const date = new Date(trimmed)
        if (!isNaN(date.getTime())) {
          const dateStr = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
          const timeStr = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs bg-purple-50 text-purple-700 whitespace-nowrap">
              {dateStr}, {timeStr}
            </span>
          )
        }
      } catch {
        // Fall through
      }
    }

    // Long text - truncate, plain
    if (trimmed.length > 80) {
      return (
        <div className="truncate max-w-[300px] text-sm" title={trimmed}>
          {trimmed}
        </div>
      )
    }

    // Short text - green badge
    if (trimmed.length < 30 && trimmed.length > 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs bg-green-50 text-green-700">
          {trimmed}
        </span>
      )
    }

    // Medium text - plain
    return <div className="text-sm">{trimmed}</div>
  }

  return <div className="text-sm">{String(value)}</div>
}

// Generate columns dynamically from data
const generateColumns = (data: Record<string, any>[]): ColumnDef<any>[] => {
  if (!data || data.length === 0) return []

  const firstRow = data[0]
  if (!firstRow) return []

  const columnKeys = Object.keys(firstRow)

  return columnKeys.map((key) => ({
    accessorKey: key,
    header: createSortableHeader(key),
    cell: ({ row }) => formatCellValue(row.getValue(key)),
  }))
}

// Export data as CSV
const exportToCSV = (data: Record<string, any>[]) => {
  if (!data || data.length === 0) {
    toast.error('No data to export')
    return
  }

  const firstRow = data[0]
  if (!firstRow) {
    toast.error('No data to export')
    return
  }

  const keys = Object.keys(firstRow)
  const csvContent = [
    keys.join(','), // Header row
    ...data.map((row) =>
      keys
        .map((key) => {
          const value = row[key]
          if (value === null || value === undefined) return ''
          if (typeof value === 'object') return JSON.stringify(value)
          const strValue = String(value)
          // Escape commas and quotes in CSV
          if (strValue.includes(',') || strValue.includes('"')) {
            return `"${strValue.replace(/"/g, '""')}"`
          }
          return strValue
        })
        .join(',')
    ),
  ].join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  triggerDownload(url, `query_results_${Date.now()}.csv`)

  toast.success('CSV exported successfully')
}

export function SqlResultsTable({
  result,
  className = '',
}: SqlResultsTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})

  // Generate columns from data
  const columns = React.useMemo(
    () => generateColumns(result.data),
    [result.data]
  )

  const table = useReactTable({
    data: result.data,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnVisibility,
    },
  })

  // Handle empty results
  if (!result.success) {
    return (
      <div className={cn('p-6', className)}>
        <div className="text-center text-red-600">
          Query failed. Check the error details.
        </div>
      </div>
    )
  }

  if (!result.data || result.data.length === 0) {
    return (
      <div className={cn('p-6', className)}>
        <div className="text-center text-gray-500">
          Query returned no results
        </div>
      </div>
    )
  }

  return (
    <div className={cn('h-full flex flex-col overflow-hidden', className)}>
      {/* Toolbar - matching DataTable py-4 px-6 spacing */}
      <div className="flex items-center justify-between gap-2 py-4 px-6 flex-shrink-0">
        <div className="flex items-center gap-2">
          {/* Filter Button (dummy for now) */}
          <Button variant="outline" size="sm" disabled className="gap-2">
            <Filter className="h-4 w-4" />
            Filter
          </Button>

          {/* Column Visibility Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }>
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Row count */}
          <div className="text-sm text-muted-foreground">
            {result.count !== undefined ? (
              <span>
                <strong>{result.count}</strong> row
                {result.count !== 1 ? 's' : ''}
              </span>
            ) : (
              <span>
                <strong>{result.data.length}</strong> row
                {result.data.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Export Button - on the right */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportToCSV(result.data)}
          className="gap-2">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Table - matching DataTable px-6 padding */}
      <div className="flex-1 min-h-0 overflow-auto px-6">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      No results found
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
